import React, { useState, useEffect, useMemo } from 'react';
import { withRouter } from "react-router-dom";
import { connect, useDispatch } from "react-redux";
import { getMarketPurchases, clearMessages } from "../../../../_actions/user_actions";
import MessageModal from '../../Utilities/MessageModal';

function LeftMenu(props) {
  const dispatch = useDispatch();
  // Initialize state to manage which submenus are open.
  // The state structure remains an object: { 'subMarket': true, 'subMoney': false }
  const [openSubmenus, setOpenSubmenus] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);

  const userData = props.data?.user.userData ?? ["Loading Data"];
  const soldItems = useMemo(() => {
    // If the data exists, use it.
    if (props.data.user?.purchases) {
        return props.data.user.purchases;
    }
    // Otherwise, return the same reference to ["Loading"] until the data is loaded.
    // By defining the fallback array outside the function or using useMemo, 
    // we prevent a new array from being created on every render.
    return ["Loading"]; 
  }, [props.data.user?.purchases]);

  useEffect(() => {
    if (userData.messages?.length > 0) {
      // If messages exist, open the modal
      setIsModalOpen(true);
    } 
    // We don't need an else, because the modal closes itself and clears messages
  }, [userData.messages]);

  // *** 3. New Function to Close Modal and Clear Redux Messages ***
  const handleCloseModal = () => {
    setIsModalOpen(false);
    // This clears the Redux state, which triggers the useEffect above to re-evaluate
    // but since messages are now empty, it won't re-open the modal immediately.
    dispatch(clearMessages());
  };

  useEffect(() => {
    if (soldItems?.count > 0) {
      if (!document.title.startsWith('🔴 ')) {
        document.title = '🔴 ' + document.title;
      }
    } else {
      document.title = document.title.replace(/^🔴\s*/, '');
    }
  }, [soldItems]);

  useEffect(() => {
    let intervalId;
    let timeoutId;
    let isMounted = true;

    const fetchData = () => {
      if (isMounted && userData.role === 1) {
        dispatch(getMarketPurchases());
      }
    };

    fetchData();

    if (userData.role === 1) {
      intervalId = setInterval(fetchData, 60000);
      timeoutId = setTimeout(() => {
        clearInterval(intervalId);
      }, 600000);
    }

    return () => {
      isMounted = false;  
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [dispatch, userData.role]);

  // Helper to directly set the open/closed state
  const handleSubmenuState = (key, isOpen) => {
    if (isOpen) {
      // If opening, ensure all others are closed (the single-open logic)
      setOpenSubmenus({ [key]: true });
    } else {
      // If closing, set only this key to false
      setOpenSubmenus(prev => ({
        ...prev,
        [key]: false
      }));
    }
  };

  const handleItemClick = () => {
    if (props.onItemClick) {
      props.onItemClick();
    }
  };

  const auctionAddress = () => {
    if (userData.role === 0) return "/student/auctions/" + userData._id;
    if (userData.role === 1) return "/teacher/auctions/" + userData._id;
    if (userData.role === 2) return "/prin/auctions/" + userData._id;
  };

  const addAddress = () => {
    if (userData.role === 0) return "/student/addClass/" + userData._id;
    if (userData.role === 2) return "/prin/approved/" + userData._id;
  };

  const addHeading = () => {
    if (userData.role === 0) return "Add Class";
    if (userData.role === 2) return "Approved Teachers";
  };

  const renderMenuItem = (item, isChild = false) => {
    if (item.children) {
      return (
        <li 
          key={item.key} 
          className={`menu-item ${isChild ? 'submenu-item' : ''}`}
          // *** ADDED MOUSE ENTER TO OPEN ***
          onMouseEnter={() => handleSubmenuState(item.key, true)}
          // *** ADDED MOUSE LEAVE TO CLOSE ***
          onMouseLeave={() => handleSubmenuState(item.key, false)}
        >
          <div // Changed from <button> to <div> since we are now using hover
            className="menu-link submenu-toggle"
            // Click is no longer required for basic open/close, but kept if user wants to click to open
            // onClick={() => handleSubmenuState(item.key, !openSubmenus[item.key])}
          >
            {item.label}
            {soldItems.count > 0 && item.key === 'subMarket' && (
              <span className="badge">{soldItems.count}</span>
            )}
            <span className={`arrow ${openSubmenus[item.key] ? 'open' : ''}`}>▼</span>
          </div>
          <ul className={`submenu ${openSubmenus[item.key] ? 'open' : ''}`}>
            {item.children.map(child => renderMenuItem(child, true))}
          </ul>
        </li>
      );
    }

    return (
      <li key={item.key} className={`menu-item ${isChild ? 'submenu-item' : ''}`}>
        <div onClick={handleItemClick}>
          {item.label}
        </div>
      </li>
    );
  };

  const Navigation = () => {
    if (!userData.isAuth || typeof userData.role !== "number") return null;

    if (userData.role === 0) return studentNavigation();
    if (userData.role === 1) return teacherNavigation();
    if (userData.role === 2) return adminNavigation();
    if (userData.role === 3) return teacherNavigation();

    return null;
  };

  const studentNavigation = () => {
    if (!userData?._id) return null;

    const items = [
      { label: (<a href="/student/users">Users</a>), key: 'users' },
      {
        label: 'Money', key: 'subMoney',
        children: [
          { label: <a href={`/student/savings/${userData._id}`}>Savings</a>, key: 'savings' },
          { label: <a href={`/student/cds/${userData._id}`}>Certificate of Deposit</a>, key: 'cds' },
          { label: <a href={`/student/cryptos/${userData._id}`}>Cryptos</a>, key: 'cryptos' },
          { label: <a href={`/student/raffle/${userData._id}`}>Raffle</a>, key: 'raffle' },
        ]
      },
      {
        label: 'Market', key: 'subMarket',
        children: [
          { label: (<a href={auctionAddress()}>View Auctions</a>), key: 'viewAuctions' },
          { label: (<a href={`/teacher/auctions/${userData._id}`}>Make Auctions</a>), key: 'makeAuctions' },
          { label: (<a href="/student/store">Store</a>), key: 'store' },
        ]
      },
      { label: (<a href={addAddress()}>{addHeading()}</a>), key: 'addClass' }
    ];

    return (
      <ul className="menu-list">
        {items.map(item => renderMenuItem(item))}
      </ul>
    );
  };

  const teacherNavigation = () => {
    const items = [
      { label: (<a href="/student/users">Users</a>), key: 'users' },
      { label: (<a href="/teacher/classes">Classes</a>), key: 'classes' },
      {
        label: 'Market',
        key: 'subMarket',
        children: [
          { label: (<a href={auctionAddress()}>Make Auctions</a>), key: 'viewAuctions' },
          { label: (<a href="/teacher/approveAuctions">Approve Auctions</a>), key: 'approveAuctions' },
          { label: (<a href="/teacher/store">Store</a>), key: 'store' },
        ],
      },
      {
        label: 'Money', key: 'subMoney',
        children: [
          { label: (<a href="/teacher/bucks">Bucks</a>), key: 'Bucks' },
          { label: (<a href="/teacher/transactions">Transactions</a>), key: 'Transactions' },
          { label: (<a href="/teacher/events">Events</a>), key: 'Events' },
        ],
      },
      { label: (<a href="/teacher/settings">Settings</a>), key: 'Settings' },
      { label: (<a href="/teacher/feature">Feature Request</a>), key: 'FeatureRequest' },
    ];

    return (
      <ul className="menu-list">
        {items.map(item => renderMenuItem(item))}
      </ul>
    );
  };

  const adminNavigation = () => {
    const items = [
      { label: (<a href="/student/users">Users</a>), key: 'users' },
      {
        label: 'Classes', key: 'subClasses',
        children: [
          { label: (<a href="/teacher/classes">My Classes</a>), key: 'classes' },
          { label: (<a href={addAddress()}>{addHeading()}</a>), key: 'addClass' },
        ],
      },
      {
        label: 'Market', key: 'subMarket',
        children: [
          { label: (<a href={auctionAddress()}>Make Auctions</a>), key: 'viewAuctions' },
          { label: (<a href="/teacher/approveAuctions">Approve Auctions</a>), key: 'approveAuctions' },
        ],
      },
      {
        label: 'Money', key: 'subMoney',
        children: [
          { label: (<a href="/teacher/bucks">Bucks</a>), key: 'Bucks' },
          { label: (<a href="/teacher/transactions">Transactions</a>), key: 'Transactions' },
          { label: (<a href="/teacher/events">Events</a>), key: 'Events' },
        ],
      },
      { label: (<a href="/prin/settings">Settings</a>), key: 'Settings' },
      { label: (<a href="/teacher/feature">Feature Request</a>), key: 'FeatureRequest' },
    ];

    return (
      <ul className="menu-list">
        {items.map(item => renderMenuItem(item))}
      </ul>
    );
  };

  return (
    <>
      <Navigation />
      {isModalOpen && (
        <MessageModal 
          messages={userData.messages} 
          onClose={handleCloseModal} 
        />
      )}
    </>
  );
}

function mapStateToProps(state) {
  return {
    data: state
  };
}

export default withRouter(connect(mapStateToProps)(LeftMenu));